import React, { useState } from 'react'
import { observer } from 'mobx-react'
import { Redirect, Route, RouteChildrenProps } from 'react-router'
import { useHoverState } from 'hooks/hover-notify'
import {
  SplitView,
  SplitViewMain,
  SplitViewSide,
  SplitViewToggleButton,
} from 'components/SplitView'
import AdminNav from '../../components/AdminNav'
import InventoryManager from '../../inventory/views/InventoryManager'
import PayoutsAdminRoot from 'payouts/admin/views/PayoutsAdminRoot'
import AdminViewModel from 'admin/AdminViewModel'
import ChangeRequests from 'admin/scope-change-requests/views/ScopeChangeRequests'
import Icon from 'components/Icon'
import styles from './Admin.sass'
import BundleManagerPage from 'admin/bundle/views/BundleManager/BundleManagerPage'
import Staffing from '../../staffing/components/Staffing'
import BookkeepingManager from 'admin/bookkeeping/views/BookkeepingManager'
import { useActivation } from 'hooks/mobx-hooks'
import MergeAccountsScreen from '../../account-linking/MergeAccountsScreen'
import MergeAccountsDialog from '../../account-linking/MergeAccountsDialog/MergeAccountsDialog'
import RejectMergeDialog from '../../account-linking/RejectMergeDialog/RejectMergeDialog'
import IgnoreMergeRequestDialog from '../../account-linking/IgnoreMergeRequestDialog/IgnoreMergeRequestDialog'

interface AdminProps extends RouteChildrenProps {
  vm: AdminViewModel
}

export default observer(function Admin({ match, vm }: AdminProps) {
  useActivation(vm)
  const [highlightBorder, setHighlightBorder] = useState(false)

  const toggleRef = React.useRef<HTMLAnchorElement | null>(null)
  const isToggleHovering = useHoverState(toggleRef, setHighlightBorder)

  return (
    <SplitView>
      <SplitViewSide
        collapsed={!vm.navVisible}
        highlightBorder={highlightBorder}
      >
        <AdminNav vm={vm} />
      </SplitViewSide>
      <SplitViewMain>
        <div className={styles.toggleButtonContainer}>
          <SplitViewToggleButtonContainer
            onAdminNavSidebarToggleClick={vm.toggleNavVisibility}
            isAdminNavSidebarVisible={vm.navVisible}
            isToggleHovering={isToggleHovering}
          />
        </div>
        <Route path={`${match?.url}`} exact>
          <Redirect push={false} to={`${match?.url}/change-requests`} />
        </Route>
        {vm.canManageBookkeeping && (
          <Route path={`${match?.url}/bookkeeping-manager`}>
            <BookkeepingManager vm={vm.bookkeepingManagerVM} />
          </Route>
        )}
        {vm.canManageJobScopeChangeRequest && (
          <Route path={`${match?.url}/change-requests`}>
            <ChangeRequests vm={vm.scopeChangeRequestsVM} />
          </Route>
        )}
        {vm.canManageServices && (
          <Route path={`${match?.url}/service-manager`}>
            <InventoryManager vm={vm.inventoryManagerVm} />
          </Route>
        )}
        {vm.canManageServices && (
          <Route path={`${match?.url}/bundle-manager`}>
            <BundleManagerPage vm={vm.bundleManagerVm} />
          </Route>
        )}
        {vm.canSeeStaffingManager && vm.staffingVm && (
          <Route path={`${match?.url}/staffing-manager`}>
            <Staffing vm={vm.staffingVm} />
          </Route>
        )}
        {vm.isPlatformAdmin && vm.payoutsAdminVm && (
          <Route path={`${match?.url}/payouts`}>
            <PayoutsAdminRoot vm={vm.payoutsAdminVm} />
          </Route>
        )}
        {vm.canManageAccountMergeRequests && (
          <Route path={`${match?.url}/merge-requests`}>
            <MergeAccountsScreen vm={vm.accountMergeRequestsVM} />
            <MergeAccountsDialog
              vm={vm.accountMergeRequestsVM.mergeAccountsDialogVM}
            />
            <RejectMergeDialog
              vm={vm.accountMergeRequestsVM.rejectMergeDialogVM}
            />
            <IgnoreMergeRequestDialog
              vm={vm.accountMergeRequestsVM.ignoreMergeRequestVM}
            />
          </Route>
        )}
      </SplitViewMain>
    </SplitView>
  )
})

interface SplitViewToggleButtonContainerProps {
  onAdminNavSidebarToggleClick?: () => unknown
  isAdminNavSidebarVisible: boolean
  isToggleHovering: boolean
  toggleRef?: React.Ref<HTMLAnchorElement>
}

export const SplitViewToggleButtonContainer = observer(
  ({
    onAdminNavSidebarToggleClick,
    isAdminNavSidebarVisible,
    isToggleHovering,
    toggleRef,
  }: SplitViewToggleButtonContainerProps) => {
    if (onAdminNavSidebarToggleClick) {
      return (
        <SplitViewToggleButton
          onClick={onAdminNavSidebarToggleClick}
          ref={toggleRef}
        >
          {isToggleHovering ? (
            isAdminNavSidebarVisible ? (
              <Icon name="hover-arrow-highlight" />
            ) : (
              <Icon name="hover-menu-highlight" />
            )
          ) : isAdminNavSidebarVisible ? (
            <Icon name="hover-arrow" />
          ) : (
            <Icon name="hover-menu" />
          )}
        </SplitViewToggleButton>
      )
    }
    return null
  },
)
